import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Marketing pages
import LandingLayout from './components/landing/LandingLayout';
import Home     from './pages/marketing/Home';
import Features from './pages/marketing/Features';
import Pricing  from './pages/marketing/Pricing';
import About    from './pages/marketing/About';
import Contact  from './pages/marketing/Contact';
import Privacy  from './pages/marketing/Privacy';
import Terms    from './pages/marketing/Terms';

// Auth
import Login          from './pages/auth/Login';
import Register       from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyOtp      from './pages/auth/VerifyOtp';

// App layout
import Layout from './components/layout/Layout';
import Dashboard        from './pages/dashboard/Dashboard';
import EmployeeList     from './pages/employees/EmployeeList';
import EmployeeDetail   from './pages/employees/EmployeeDetail';
import CycleList        from './pages/cycles/CycleList';
import CycleDetail      from './pages/cycles/CycleDetail';
import ReviewList       from './pages/reviews/ReviewList';
import ReviewForm       from './pages/reviews/ReviewForm';
import ReviewTemplates  from './pages/reviews/ReviewTemplates';
import CalibrationBoard from './pages/calibration/CalibrationBoard';
import ScoringScreen    from './pages/scoring/ScoringScreen';
import PipTracker       from './pages/pip/PipTracker';
import PipDetail        from './pages/pip/PipDetail';
import GoalsDashboard   from './pages/goals/GoalsDashboard';
import FeedbackHub      from './pages/feedback/FeedbackHub';
import SurveyDashboard  from './pages/surveys/SurveyDashboard';
import SurveyRespond    from './pages/surveys/SurveyRespond';
import SurveyResults    from './pages/surveys/SurveyResults';
import Analytics        from './pages/analytics/Analytics';
import Compensation     from './pages/compensation/Compensation';
import Settings         from './pages/settings/Settings';
import Meetings         from './pages/meetings/Meetings';
import MeetingDetail    from './pages/meetings/MeetingDetail';
import HrisHome         from './pages/hris/HrisHome';
import Succession       from './pages/succession/Succession';
import JobBoardHome     from './pages/jobboard/JobBoardHome';
import PostingDetail    from './pages/jobboard/PostingDetail';
import Learning         from './pages/learning/Learning';
import Integrations     from './pages/integrations/Integrations';
import Webhooks         from './pages/webhooks/Webhooks';
import DataHealth       from './pages/datahealth/DataHealth';
import AiInsights       from './pages/ai/AiInsights';
import Benchmarks        from './pages/benchmarks/Benchmarks';
import WorkforcePlanning from './pages/workforce/WorkforcePlanning';
import Payroll           from './pages/payroll/Payroll';
import PayrollDetail     from './pages/payroll/PayrollDetail';
import RunEngine         from './pages/payroll/engine/RunEngine';
import EmployeePayroll   from './pages/payroll/employee/EmployeePayroll';
import MyPayroll         from './pages/payroll/employee/MyPayroll';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', height:'100vh', alignItems:'center', justifyContent:'center', background:'#07080f' }}>
      <div style={{ width:32, height:32, border:'3px solid #1a72ff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin .8s linear infinite' }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
}

function RequireGuest({ children }) {
  const { user } = useAuth();
  return user ? <Navigate to="/app" replace /> : children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Marketing */}
          <Route element={<LandingLayout />}>
            <Route path="/"         element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing"  element={<Pricing />} />
            <Route path="/about"    element={<About />} />
            <Route path="/contact"  element={<Contact />} />
            <Route path="/privacy"  element={<Privacy />} />
            <Route path="/terms"    element={<Terms />} />
          </Route>

          {/* Auth */}
          <Route path="/login"      element={<RequireGuest><Login /></RequireGuest>} />
          <Route path="/register"   element={<RequireGuest><Register /></RequireGuest>} />
          <Route path="/verify-otp" element={<RequireGuest><VerifyOtp /></RequireGuest>} />
          <Route path="/forgot"     element={<RequireGuest><ForgotPassword /></RequireGuest>} />

          {/* App */}
          <Route path="/app" element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index                              element={<Dashboard />} />
            <Route path="employees"                   element={<EmployeeList />} />
            <Route path="employees/:id"               element={<EmployeeDetail />} />
            <Route path="cycles"                      element={<CycleList />} />
            <Route path="cycles/:id"                  element={<CycleDetail />} />
            <Route path="cycles/:cycleId/calibration" element={<CalibrationBoard />} />
            <Route path="cycles/:cycleId/scoring"     element={<ScoringScreen />} />
            <Route path="reviews"                     element={<ReviewList />} />
            <Route path="reviews/templates"           element={<ReviewTemplates />} />
            <Route path="reviews/:id"                 element={<ReviewForm />} />
            <Route path="pips"                        element={<PipTracker />} />
            <Route path="pips/:id"                    element={<PipDetail />} />
            <Route path="goals"                       element={<GoalsDashboard />} />
            <Route path="feedback"                    element={<FeedbackHub />} />
            <Route path="surveys"                     element={<SurveyDashboard />} />
            <Route path="surveys/:id/respond"         element={<SurveyRespond />} />
            <Route path="surveys/:id/results"         element={<SurveyResults />} />
            <Route path="analytics"                   element={<Analytics />} />
            <Route path="ai-insights"                 element={<AiInsights />} />
            <Route path="compensation"                element={<Compensation />} />
            <Route path="benchmarks"                  element={<Benchmarks />} />
            <Route path="workforce"                   element={<WorkforcePlanning />} />
            <Route path="payroll"                     element={<Payroll />} />
            <Route path="payroll/:id"                 element={<PayrollDetail />} />
            <Route path="payroll/:id/engine"          element={<RunEngine />} />
            <Route path="employees/:id/payroll"       element={<EmployeePayroll />} />
            <Route path="my-payroll"                  element={<MyPayroll />} />
            <Route path="meetings"                    element={<Meetings />} />
            <Route path="meetings/:id"                element={<MeetingDetail />} />
            <Route path="hris"                        element={<HrisHome />} />
            <Route path="succession"                  element={<Succession />} />
            <Route path="job-board"                   element={<JobBoardHome />} />
            <Route path="job-board/:id"               element={<PostingDetail />} />
            <Route path="learning"                    element={<Learning />} />
            <Route path="learning/:tab"               element={<Learning />} />
            <Route path="integrations"                element={<Integrations />} />
            <Route path="webhooks"                    element={<Webhooks />} />
            <Route path="data-health"                 element={<DataHealth />} />
            <Route path="settings"                    element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
